import type { InternalPage } from '../lib/pages.ts'

/**
 * Every page in the internal webapp, in the order the rail shows them.
 *
 * This file belongs to the apps. It sits under `src/routes/` rather than in
 * `src/lib/` because every page ever added to this repo had to edit the list,
 * and a page should touch its own tree rather than the framework's. The shape
 * and the grouping logic stay in `src/lib/pages.ts`, which is framework.
 *
 * The `-` prefix is TanStack Router's ignore prefix, so this file adds no
 * route. Add an entry when you add a route under `src/routes/<app>/`, and
 * `-nav.test.ts` fails if you forget.
 *
 * **Every page carries a group, and the group matches the first segment of
 * the path.** A reader navigates `/payments/ach-returns` and looks for it
 * under Payments; a rail that listed it flat would answer a question the URL
 * had already answered differently. `-nav.test.ts` holds the two halves in
 * step, so a page added under a new app folder fails until it names a group.
 *
 * `GET /viewer-access` decides which entries the signed-in employee sees.
 * They can then hide an allowed entry in customize mode (ADR-0014).
 * The backend also protects each restricted page's API data.
 *
 * Every entry carries a `description`: one sentence on what the reader sees
 * and the question the page answers. The landing page prints it, and page
 * search matches it.
 */
export const INTERNAL_PAGES: readonly InternalPage[] = Object.freeze([
  {
    id: 'blytzcollect-analytics',
    path: '/blytzcollect/analytics',
    title: 'BlytzCollect analytics',
    description:
      'How the outbound collections AI agent did: calls, billed time, transfers, and whether those calls collected a payment.',
    group: 'BlytzCollect'
  },
  {
    id: 'ach-returns',
    path: '/payments/ach-returns',
    title: 'ACH returns',
    description:
      'ACH charges and returns by code for a period, and whether the unauthorized return rate is over the NACHA ceiling.',
    group: 'Payments'
  },
  {
    id: 'bin-mcc-fee-report',
    path: '/payments/bin-mcc-fee-report',
    title: 'BIN / MCC fee report',
    description:
      'Amaryllis processing cost by card BIN and MCC, and the volume those cards processed.',
    group: 'Payments'
  },
  {
    id: 'multicard-usage',
    path: '/payments/multicard-usage',
    title: 'Multicard usage',
    description:
      'Payment items a customer split across more than one card, by merchant.',
    group: 'Payments'
  },
  {
    id: 'retry-analysis',
    path: '/payments/retry-analysis',
    title: 'Retry analysis',
    description:
      'First-attempt payment failures and their retries, with credit cards and Amex left out.',
    group: 'Payments'
  },
  {
    id: 'ai-texts',
    path: '/reports/ai-texts',
    title: 'AI texts',
    description: 'Every outbound AI text, and what that message led to.',
    group: 'Reports'
  },
  {
    id: 'cs-tools-autopay-accounts',
    path: '/cs-tools/autopay-accounts',
    title: 'Autopay accounts',
    description:
      "The autopay configurations on one merchant's accounts, once you pick that merchant.",
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-merchant-notes',
    path: '/cs-tools/merchant-notes',
    title: 'Merchant notes',
    description:
      'What the team has recorded about a merchant, and a place for any employee to add a note.',
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-registrations',
    path: '/cs-tools/registrations',
    title: 'Registrations',
    description:
      'New customer accounts by merchant, by the day the customer accepted terms.',
    group: 'CS Tools'
  },
  {
    id: 'payment-report',
    path: '/cs-tools/payment-report',
    title: 'Payment report',
    description:
      'Individual payments by merchant, by the day they were processed.',
    group: 'CS Tools'
  },
  {
    id: 'message-report',
    path: '/cs-tools/message-report',
    title: 'Message report',
    description:
      "Every message sent to a merchant's customers, by batch, one by one, and in total.",
    group: 'CS Tools'
  },
  {
    id: 'undelivered',
    path: '/cs-tools/undelivered',
    title: 'Undelivered',
    description:
      'Outbound texts the messaging vendor could not deliver, by the day they were sent.',
    group: 'CS Tools'
  },
  {
    id: 'opt-outs',
    path: '/cs-tools/opt-outs',
    title: 'Opt-outs',
    description:
      'Customer communication opt-outs by merchant, by the day they last changed.',
    group: 'CS Tools'
  },
  {
    id: 'reconciliation',
    path: '/cs-tools/reconciliation',
    title: 'Reconciliation',
    description:
      'Payment failures that are still waiting for a person to resolve them.',
    group: 'CS Tools'
  },
  {
    id: 'merchant-settings',
    path: '/reports/merchant-settings',
    title: 'Merchant settings',
    description:
      "Every merchant's account settings and feature switches, as they stand today.",
    group: 'Reports'
  },
  {
    id: 'collections-scorecard',
    path: '/cs-tools/collections-scorecard',
    title: 'Collections scorecard',
    description:
      "How late each merchant's customers pay the invoices due in the range, how many never do, and the autopay, texts, and BlytzCollect calls behind that.",
    group: 'CS Tools'
  },
  {
    id: 'propensity-to-pay',
    path: '/cs-tools/propensity-to-pay',
    title: 'Propensity to pay',
    description:
      'How likely one account is to pay today, this week, and over the next 30 days, and to promise in those 30 days.',
    group: 'CS Tools'
  },
  {
    id: 'propensity-accuracy',
    path: '/cs-tools/propensity-accuracy',
    title: 'Propensity accuracy',
    description:
      "The daily job's pay scores for past-due accounts, beside what each account did in the next 30 days.",
    group: 'CS Tools'
  },
  {
    id: 'report-chat',
    path: '/lab/report-chat',
    title: 'Report chat',
    description:
      'A conversation that turns a question into a report draft, with that draft beside the chat.',
    group: 'Lab'
  },
  {
    id: 'report-drafts',
    path: '/lab/report-drafts',
    title: 'Report drafts',
    description:
      'The report drafts your chats built, newest first. Nobody has checked their figures.',
    group: 'Lab'
  },
  {
    // Opens only from a link, because it needs a draft id.
    id: 'report-draft',
    path: '/lab/report-draft',
    title: 'Report draft',
    description:
      'One report a chat drafted, opened from a link that names that draft.',
    group: 'Lab',
    inRail: false
  },
  {
    id: 'alerts-rules',
    path: '/alerts/rules',
    title: 'My alerts',
    description:
      'The Slack DMs and emails you asked for, which only you can see.',
    group: 'Alerts',
    // Not a report, so the rail pins it above the account row.
    pinned: true,
    icon: 'bell'
  },
  {
    id: 'ai-decline-impact',
    path: '/reports/ai-decline-impact',
    title: 'AI decline impact',
    description:
      'Whether the AI decline texts got declined payments paid sooner or more often.',
    group: 'Reports'
  },
  {
    id: 'sales-tools-pricing-studio',
    path: '/sales-tools/pricing-studio',
    title: 'Pricing Studio',
    description:
      'A deal priced from the calculator, options compared side by side, and a proposal you can print.',
    group: 'Sales Tools'
  },
  {
    id: 'platform-jobs',
    path: '/platform/jobs',
    title: 'Ingestion jobs',
    description: 'Every ingestion job, and the runs recorded for each one.',
    group: 'Platform'
  }
])
